import { X, Library, User, Settings, ChevronLeft, LogIn, LogOut } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { useUserStats } from '../hooks/useUserStats';

import { AuthModal } from './AuthModal';

interface RightSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RightSidebar({ isOpen, onClose }: RightSidebarProps) {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { stats, loading: statsLoading } = useUserStats();
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  const handleAuthClick = () => {
    setShowAuthModal(true);
  };

  if (!isOpen && !isAnimating) {return null;}

  const menuItems = [
    { path: '/library', icon: Library, label: 'My Collection' },
    { path: '/profile', icon: User, label: 'Profile' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <>
      {/* Overlay */}
      <div 
        className={`sidebar-overlay ${isOpen ? 'fade-in' : 'opacity-0'} transition-opacity duration-300`}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <nav 
        className={`sidebar-right ${isOpen ? 'slide-in-right' : 'slide-out-right'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Menu</h2>
            {user && (
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {user.email}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="tap-target flex items-center justify-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
          </button>
        </div>
        
        {/* Navigation Items */}
        <div className="p-4 space-y-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                  ${isActive 
                    ? 'bg-neutral-100 dark:bg-neutral-800' 
                    : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                  }
                `}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'accent' : 'text-neutral-600 dark:text-neutral-400'}`} />
                <span className={`font-medium ${isActive ? 'accent text-neutral-900 dark:text-neutral-100' : 'text-neutral-700 dark:text-neutral-300'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
          
          {/* Auth Button */}
          <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
            {user ? (
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800/50 w-full text-left"
              >
                <LogOut className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                <span className="font-medium text-neutral-700 dark:text-neutral-300">Sign Out</span>
              </button>
            ) : (
              <button
                onClick={handleAuthClick}
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800/50 w-full text-left"
              >
                <LogIn className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                <span className="font-medium text-neutral-700 dark:text-neutral-300">Sign In</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Quick Stats - Only show when user is logged in and has actual data */}
        {user && stats && !statsLoading && (stats.totalBirdsIdentified > 0 || stats.totalDetectionSessions > 0) && (
          <div className="mt-8 mx-4 p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl">
            <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-3">
              Your Stats
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Birds Identified</span>
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{stats.totalBirdsIdentified}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Detection Sessions</span>
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{stats.totalDetectionSessions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Rare Finds</span>
                <span className="text-sm font-medium accent text-emerald-600 dark:text-emerald-400">{stats.rareBirds}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Swipe hint */}
        <div className="absolute bottom-8 left-4 right-4 flex items-center justify-center text-xs text-neutral-400 dark:text-neutral-600">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Swipe left to close
        </div>
      </nav>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
}